import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Check, X, Clock, Link2, Share2, ExternalLink } from "lucide-react";

export default function AdminReferralTab() {
  const [data, setData] = useState({ share_claims: [], backlink_claims: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referral/admin/claims`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAction = async (userId, action, type, domain) => {
    try {
      await axios.post(`${API}/referral/admin/action`, {
        request_id: userId,
        action,
        note: domain || "",
      }, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-emerald-100 text-emerald-800",
      rejected: "bg-red-100 text-red-800",
    };
    return <span className={`text-xs px-2 py-0.5 font-bold ${map[status] || map.pending}`}>{status}</span>;
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const pendingShares = data.share_claims.filter(c => c.status === "pending").length;
  const pendingBacklinks = data.backlink_claims.filter(c => c.status === "pending").length;

  return (
    <div className="space-y-8" data-testid="admin-referral-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Share Claims</p>
          <p className="text-3xl font-mono font-bold text-gray-900">{data.share_claims.length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Pending Shares</p>
          <p className="text-3xl font-mono font-bold text-yellow-600">{pendingShares}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Backlink Claims</p>
          <p className="text-3xl font-mono font-bold text-gray-900">{data.backlink_claims.length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Pending Backlinks</p>
          <p className="text-3xl font-mono font-bold text-yellow-600">{pendingBacklinks}</p>
        </div>
      </div>

      {/* Share Claims */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Share2 className="w-5 h-5" /> Share Claims (5x = 1 week free)
        </h3>
        {data.share_claims.length === 0 ? (
          <p className="text-gray-400 text-sm">No share claims yet.</p>
        ) : (
          <div className="space-y-2">
            {data.share_claims.map((claim, i) => (
              <div key={i} className="bg-white border border-gray-200 p-4 rounded-lg flex items-center justify-between gap-4" data-testid={`admin-share-claim-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{claim.email}</p>
                  <p className="text-xs text-gray-400">Reward: {claim.reward_days} days | {claim.created_at?.slice(0, 10)}</p>
                  {claim.proof_url && (
                    <a href={claim.proof_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                      <ExternalLink className="w-3 h-3" /> Proof
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(claim.status)}
                  {claim.status === "pending" && (
                    <>
                      <button
                        data-testid={`approve-share-${i}`}
                        onClick={() => handleAction(claim.user_id, "approve", "share")}
                        className="p-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded"
                      ><Check className="w-4 h-4" /></button>
                      <button
                        data-testid={`reject-share-${i}`}
                        onClick={() => handleAction(claim.user_id, "reject", "share")}
                        className="p-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded"
                      ><X className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Backlink Claims */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5" /> Backlink Claims (1 backlink = 2 weeks free)
        </h3>
        {data.backlink_claims.length === 0 ? (
          <p className="text-gray-400 text-sm">No backlink claims yet.</p>
        ) : (
          <div className="space-y-2">
            {data.backlink_claims.map((bl, i) => (
              <div key={i} className="bg-white border border-gray-200 p-4 rounded-lg flex items-center justify-between gap-4" data-testid={`admin-backlink-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{bl.email} — <span className="text-blue-600">{bl.domain}</span></p>
                  <p className="text-xs text-gray-400 truncate">{bl.page_url}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {bl.auto_verified ? (
                      <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1"><Check className="w-3 h-3" /> Auto-verified</span>
                    ) : (
                      <span className="text-[10px] text-yellow-600 font-bold flex items-center gap-1"><Clock className="w-3 h-3" /> Not auto-verified</span>
                    )}
                    {bl.verify_error && <span className="text-[10px] text-red-500">{bl.verify_error}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(bl.status)}
                  {bl.status === "pending" && (
                    <>
                      <button
                        data-testid={`approve-backlink-${i}`}
                        onClick={() => handleAction(bl.user_id, "approve", "backlink", bl.domain)}
                        className="p-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded"
                      ><Check className="w-4 h-4" /></button>
                      <button
                        data-testid={`reject-backlink-${i}`}
                        onClick={() => handleAction(bl.user_id, "reject", "backlink", bl.domain)}
                        className="p-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded"
                      ><X className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
